import { motion } from "framer-motion";
import { Quote } from "lucide-react";

const testimonials = [
  {
    quote: "Saved me 3 hours of boilerplate. Pasted my Stripe spec and had a working MCP server in under a minute.",
    author: "Alex R.",
    role: "Backend Engineer",
    avatar: "AR",
    color: "bg-blue-500",
  },
  {
    quote: "This is exactly what the AI tooling space needed. Works perfectly with Cursor out of the box.",
    author: "Priya K.",
    role: "AI Engineer",
    avatar: "PK",
    color: "bg-violet-500",
  },
  {
    quote: "Converted our internal REST API to MCP in seconds. Claude can now call all our endpoints directly.",
    author: "James T.",
    role: "Full-Stack Dev",
    avatar: "JT",
    color: "bg-emerald-500",
  },
  {
    quote: "The URL fetch feature is brilliant — just pointed it at our Swagger docs and it worked first try.",
    author: "Sofia M.",
    role: "Platform Engineer",
    avatar: "SM",
    color: "bg-rose-500",
  },
  {
    quote: "We use it for every new microservice we ship. MCP server ready in literally one paste.",
    author: "David L.",
    role: "DevOps Lead",
    avatar: "DL",
    color: "bg-amber-500",
  },
  {
    quote: "Best free tool in the MCP ecosystem. The hosted endpoint means I don't need to run anything locally.",
    author: "Nina W.",
    role: "Indie Developer",
    avatar: "NW",
    color: "bg-sky-500",
  },
  {
    quote: "Tried three other converters before this. None of them handled our nested schemas. This one nailed it.",
    author: "Marcus C.",
    role: "API Architect",
    avatar: "MC",
    color: "bg-indigo-500",
  },
  {
    quote: "Shared it in our dev Slack. Half the team was using it the same day. Genuinely game-changing.",
    author: "Yuki H.",
    role: "Senior Engineer",
    avatar: "YH",
    color: "bg-fuchsia-500",
  },
];

// Duplicate for seamless loop
const doubledTestimonials = [...testimonials, ...testimonials];

const TestimonialCard = ({
  quote,
  author,
  role,
  avatar,
  color,
}: (typeof testimonials)[0]) => (
  <div className="flex-shrink-0 w-[320px] glass-strong rounded-2xl p-6 shadow-premium mx-1">
    <Quote className="w-5 h-5 text-primary/40 mb-3" />
    <p className="text-sm text-foreground leading-relaxed mb-5 line-clamp-4">{quote}</p>
    <div className="flex items-center gap-3">
      <div
        className={`w-9 h-9 rounded-full ${color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}
      >
        {avatar}
      </div>
      <div>
        <div className="text-sm font-semibold text-foreground">{author}</div>
        <div className="text-xs text-muted-foreground">{role}</div>
      </div>
    </div>
  </div>
);

const Testimonials = () => {
  return (
    <section className="py-20 overflow-hidden">
      {/* Section header */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-center mb-12 px-6"
      >
        <div className="inline-flex items-center gap-1.5 mb-3">
          <span className="text-xs font-semibold text-primary uppercase tracking-widest">Loved by devs</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
          What Developers Are Saying
        </h2>
        <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">
          Developers across the world rely on API→MCP to ship AI integrations faster.
        </p>
      </motion.div>

      {/* Scrolling marquee — fade edges */}
      <div className="relative">
        {/* Left fade */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-background to-transparent" />
        {/* Right fade */}
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-background to-transparent" />

        <div className="overflow-hidden px-2 py-2">
          <div className="marquee-track">
            {doubledTestimonials.map((t, i) => (
              <TestimonialCard key={`${t.author}-${i}`} {...t} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
